import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  signal,
  viewChild,
} from "@angular/core";
import { TypeDashboard } from "../../enums/dashboard-type.enum";
import { DashboardComponent } from "../../components/dashboard/dashboard.component";
import { CommonModule } from "@angular/common";
import { SharedModule } from "../../../../shared/shared.module";
import { DashboardFacadeService } from "../../services/dashboard-facade.service";
import { WidgetDataFacadeService } from "../../services/widget-data-facade.service";
import { MatTooltipModule } from "@angular/material/tooltip";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { DisplayService } from "../../../home/services/display.service";
import { ArticlesModule } from "../../../articles/articles.module";
import { DashboardSettingsPanelComponent } from "../dashboard-settings-panel/dashboard-settings-panel.component";
import { JustupPageComponent } from "../../../../shared/components/justup-page/justup-page.component";
import { BigButtonComponent } from "../../../../shared/components/big-button/big-button.component";

@Component({
  selector: "vl-dashboard-container",
  standalone: true,
  imports: [
    DashboardComponent,
    MatTooltipModule,
    CommonModule,
    SharedModule,
    ArticlesModule,
    JustupPageComponent,
    BigButtonComponent,
  ],
  templateUrl: "./dashboard-container.component.html",
  styleUrls: ["./dashboard-container.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardContainerComponent implements OnDestroy {
  dialogService = inject(DialogsService);
  displayService = inject(DisplayService);
  private dashboardFacade = inject(DashboardFacadeService);
  private widgetDataFacade = inject(WidgetDataFacadeService);
  private ngZone = inject(NgZone);

  /**
   * Cadence de l'auto-refresh léger (cf. `softRefresh`). Garde le dashboard
   * « live » sur un écran toujours allumé (mur de prod) sans le coût du
   * refresh complet. N'impacte pas `LIVE_PRODUCTION` qui gère son propre
   * polling 5 s (skip dans le switch de `widget-container`).
   */
  private static readonly AUTO_REFRESH_MS = 60_000;
  private autoRefreshHandle: ReturnType<typeof setInterval> | null = null;

  refreshing = signal(false);

  customLoading = input<boolean>(false);
  containerRef = viewChild<ElementRef>("container");
  containerElement = computed(() => {
    const el = this.containerRef()?.nativeElement;
    return el?.closest(".justup-page-main-content") ?? el;
  });

  rowHeight = input<number>(150);

  type = input<TypeDashboard>();

  dashboard = this.dashboardFacade.getState().dashboard;
  editable = input<boolean>(true);

  constructor() {
    effect(() => {
      this.dashboardFacade.loadDashboard(this.type());
    });
    this.startAutoRefresh();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  ngOnDestroy() {
    this.stopAutoRefresh();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  /** Onglet caché → on coupe le poll (zéro conso) ; retour visible → re-fetch
   *  immédiat + relance du poll. */
  private onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      this.widgetDataFacade.softRefresh();
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  };

  private startAutoRefresh() {
    this.stopAutoRefresh();
    if (document.visibilityState !== "visible") return;
    // Hors zone Angular : un setInterval en zone garderait l'app perpétuellement
    // « instable ». Le softRefresh ne fait que muter un signal → les effects des
    // widgets se replanifient sans dépendre de la zone.
    this.ngZone.runOutsideAngular(() => {
      this.autoRefreshHandle = setInterval(
        () => this.widgetDataFacade.softRefresh(),
        DashboardContainerComponent.AUTO_REFRESH_MS,
      );
    });
  }

  private stopAutoRefresh() {
    if (this.autoRefreshHandle !== null) {
      clearInterval(this.autoRefreshHandle);
      this.autoRefreshHandle = null;
    }
  }

  openSettings() {
    this.dialogService.openPanel(DashboardSettingsPanelComponent, {
      type: this.type(),
    });
  }

  /** Vide le cache widget côté back et recharge tous les widgets affichés. */
  async onRefresh() {
    if (this.refreshing()) return;
    this.refreshing.set(true);
    try {
      await this.widgetDataFacade.refreshAll();
    } finally {
      this.refreshing.set(false);
    }
  }
}
